import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Page, Head, Title, Lede, Tabs, Tab, Cards, Card, CardTitle, CardNote,
  DefList, Steps, Table, Scroller, CheckRow, CheckMark, CheckBody, Banner, Button, Muted
} from './TimetableGuide.styles';
import Timetable from './Timetable';
import CampusTimetableTeacher from '../CampusTimetableTeacher';
import { getTimetable } from '../../../api/timetableApi';
import { getMyProfile } from '../../../api/userProfileApi';

/**
 * Timetable guide + live check of the demo data.
 *
 * The two timetables this page explains read from live course groups, and the only campuses
 * that have any are the real ones — which is exactly why they were hard to sign off:
 * every click is a click on somebody's actual Saturday. `scripts/portal/seed_timetable_demo.py`
 * builds a parallel schedule in the Test Demo campus with the awkward cases deliberately
 * planted in it (a room double-booked, a teacher in two places, a class called off, one
 * moved, a trial for someone who is not a student yet).
 *
 * This page is the other half of that: it states what should be there, then goes and looks.
 * A failing row is either a real regression or stale demo data — the seed anchors its one-off
 * lessons to the week it ran in, so a month later they are simply in the past. The banner
 * says which of the two it is, because "the cancelled class is missing" reads like a bug
 * long before it occurs to anyone that the fixture expired.
 *
 * Tabs render the real pages, not screenshots, so resizing the window here is the same test
 * as resizing it on the pages themselves.
 */

const ADMIN_ROLES = ['portal_developer', 'portal_system_admin', 'portal_director', 'portal_manager', 'portal_admin'];

const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

const startOfWeek = (date) => {
  const result = new Date(date);
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const unique = (values) => [...new Set(values.filter(Boolean))];

/**
 * One row of the self-check.
 *
 * `test` gets both fetched weeks and returns whether the scenario is present plus a line
 * of evidence, so a pass is readable as "here it is", not just a green dot. `dated` marks
 * the checks that depend on the one-off lessons, which are the ones that go stale.
 */
const CHECKS = [
  {
    key: 'loaded',
    label: 'The week has classes in it',
    hint: 'Everything below is read from this same fetch.',
    test: ({ week }) => ({
      ok: week.occurrences.length > 0,
      detail: `${week.occurrences.length} classes · ${week.rooms.length} rooms · ${week.teachers.length} teachers`
    })
  },
  {
    key: 'roomClash',
    label: 'A double-booked room is flagged',
    hint: 'Two classes in one room at overlapping times. Try the Clashes only filter.',
    test: ({ week }) => {
      const hits = week.occurrences.filter(o => (o.conflicts || []).includes('room'));
      return {
        ok: hits.length > 0,
        detail: hits.length
          ? `${hits[0].date} ${hits[0].room} · ${hits.map(o => `${o.startTime}–${o.endTime} ${o.title}`).join('  ×  ')}`
          : 'No room clash in this week.'
      };
    }
  },
  {
    key: 'teacherClash',
    label: 'A teacher booked twice is flagged',
    hint: 'One person timetabled into two rooms at once.',
    test: ({ week }) => {
      const hits = week.occurrences.filter(o => (o.conflicts || []).includes('teacher'));
      const who = hits[0]?.teachers?.[0]?.name;
      return {
        ok: hits.length > 0,
        detail: hits.length
          ? `${hits[0].date} ${who} · ${hits.map(o => `${o.startTime}–${o.endTime} ${o.room || 'no room'}`).join('  ×  ')}`
          : 'No teacher clash in this week.'
      };
    }
  },
  {
    key: 'cancelled',
    label: 'A called-off class is kept, not deleted',
    dated: true,
    hint: 'It stays on the grid struck through. A class that vanishes cannot be asked about.',
    test: ({ week }) => {
      const hit = week.occurrences.find(o => o.status === 'cancelled');
      return {
        ok: Boolean(hit),
        detail: hit ? `${hit.date} ${hit.startTime} ${hit.title} — ${hit.notes || 'no note'}` : 'None this week.'
      };
    }
  },
  {
    key: 'noShow',
    label: 'A no-show is flagged without moving the class',
    dated: true,
    hint: 'The class stays where it is; only its status changes.',
    test: ({ week }) => {
      const hit = week.occurrences.find(o => String(o.status).startsWith('no_show'));
      return {
        ok: Boolean(hit),
        detail: hit ? `${hit.date} ${hit.startTime} ${hit.title} — ${hit.status}` : 'None this week.'
      };
    }
  },
  {
    key: 'moved',
    label: 'A class moved to another day still resolves',
    dated: true,
    hint: 'The override records the date it replaces, so one week can move without touching the term.',
    test: ({ week }) => {
      const hit = week.occurrences.find(o => o.isException && o.originalDate && o.originalDate !== o.date);
      return {
        ok: Boolean(hit),
        detail: hit
          ? `${hit.title}: ${hit.originalDate} → ${hit.date} ${hit.room || 'no room'}`
          : 'Nothing moved this week.'
      };
    }
  },
  {
    key: 'oneOffs',
    label: 'Trial, makeup and assessment all present',
    dated: true,
    hint: 'One-off lessons that belong to no course group. A trial carries a guest name instead of a student.',
    test: ({ week }) => {
      const found = unique(week.occurrences.map(o => o.lessonType).filter(t => t && t !== 'regular'));
      const wanted = ['trial', 'makeup', 'assessment'];
      const missing = wanted.filter(t => !found.includes(t));
      const guest = week.occurrences.find(o => o.guestName)?.guestName;
      return {
        ok: missing.length === 0,
        detail: missing.length
          ? `Missing: ${missing.join(', ')}`
          : `trial (${guest || 'guest'}), makeup, assessment`
      };
    }
  },
  {
    key: 'types',
    label: 'Group, 1:1 and 1:2–3 classes all render',
    hint: 'Capacity comes from the class type, so the enrolment count means something different in each.',
    test: ({ week }) => {
      const found = unique(week.occurrences.map(o => o.typeName));
      return {
        ok: found.length >= 3,
        detail: found.length ? found.join(' · ') : 'No class types resolved.'
      };
    }
  },
  {
    key: 'noRoom',
    label: 'A class with no room does not break the grid',
    hint: 'Online classes have no room. They should not invent a column.',
    test: ({ week }) => {
      const hits = week.occurrences.filter(o => !o.roomKey);
      return {
        ok: hits.length > 0,
        detail: hits.length ? `${hits.length} class(es), e.g. ${hits[0].title}` : 'Every class has a room this week.'
      };
    }
  },
  {
    key: 'specialTerm',
    label: 'A special-term class shows on its own dates',
    hint: 'Term 0 has explicit dates rather than a weekly rule.',
    test: ({ week }) => {
      // A standalone lesson belongs to no course group, so it carries a null term — which
      // Number() turns into 0. Requiring a course group is what separates a genuine
      // special-term class from every trial and makeup in the week.
      const hits = week.occurrences.filter(o => o.courseGroupId && Number(o.term) === 0);
      return {
        ok: hits.length > 0,
        detail: hits.length
          ? `${unique(hits.map(o => o.title)).join(', ')} on ${unique(hits.map(o => o.date)).join(', ')}`
          : 'No special-term class falls in this week.'
      };
    }
  },
  {
    key: 'holiday',
    label: 'A holiday week is skipped',
    hint: 'Compares this week against the week after next: some classes should simply not run.',
    test: ({ week, later }) => {
      const recurring = (data) => new Set(
        data.occurrences.filter(o => !o.isException && Number(o.term) !== 0).map(o => o.title)
      );
      const now = recurring(week);
      const then = recurring(later);
      const skipped = [...now].filter(title => !then.has(title));
      return {
        ok: skipped.length > 0,
        detail: skipped.length
          ? `Not running that week: ${skipped.join(', ')}`
          : 'Every class runs in both weeks.'
      };
    }
  }
];

const VIEWS = [
  ['Room View', 'Which room is free? One day at a time, one column per room. The default for front desk.'],
  ['Teacher View', 'Who is free? Same day, one column per teacher, so a cover can be found by eye.'],
  ['Per Room', 'One room or one teacher across the whole week — the view for "when does Amy actually work?".'],
  ['Week View', 'Seven days at once. Shape of the week rather than the detail of one day.'],
  ['List View', 'A flat sortable table. Search, and the only view that exports CSV.']
];

const STATUSES = [
  ['Confirmed', 'The default. A recurring class with nothing recorded against it is confirmed.'],
  ['Cancelled', 'Struck through and kept. The slot counts as free for clash detection.'],
  ['Pending', 'Booked but not agreed with the family yet.'],
  ['No show', 'Student or teacher did not turn up. The class stays where it was.']
];

const TYPES = [
  ['Regular', 'A normal occurrence of a course group.'],
  ['Trial', 'A prospective student. Carries a guest name because they are not in the student list yet.'],
  ['Makeup', 'A replacement for a missed class, usually on a different day.'],
  ['Assessment', 'A placement or progress test occupying a room like any other class.']
];

const ACCOUNTS = [
  ['test_manager', 'portal_manager', 'Campus management — full editing, Test Demo only.'],
  ['test_admin', 'portal_admin', 'Front desk — same editing rights, same single campus.'],
  ['demo_teacher_amy', 'portal_teacher', 'Heaviest load, four rooms, and the Thursday teacher clash.'],
  ['demo_teacher_ben', 'portal_teacher', 'Maths, including a 1:1 and a 1:2–3.'],
  ['demo_teacher_chloe', 'portal_teacher', 'Mostly Saturday — the busiest day to read on a phone.'],
  ['demo_teacher_dan', 'portal_teacher', 'Lightest load; covers one of Amy’s classes this week.'],
  ['test_teacher', 'portal_teacher', 'The pre-existing account, including the class with no room.']
];

const TimetableGuide = () => {
  const auth = useAuth();
  const roles = useMemo(() => auth.user?.profile?.['cognito:groups'] || [], [auth.user]);
  const canEdit = useMemo(() => roles.some(role => ADMIN_ROLES.includes(role)), [roles]);
  const isTeacher = roles.includes('portal_teacher');

  const [tab, setTab] = useState('guide');
  const [profile, setProfile] = useState(null);
  const [results, setResults] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');

  const runChecks = useCallback(async () => {
    setChecking(true);
    setCheckError('');
    try {
      const monday = startOfWeek(new Date());
      const later = addDays(monday, 14);
      const range = (start) => ({ fromDate: toDateKey(start), toDate: toDateKey(addDays(start, 6)) });
      // Empty campusIds means "every campus I may see", which for a campus-bound account is
      // their own — so this checks whatever campus the tester is actually signed in to.
      const [weekData, laterData] = await Promise.all([
        getTimetable({ campusIds: [], ...range(monday) }),
        getTimetable({ campusIds: [], ...range(later) })
      ]);
      setResults({
        week: weekData,
        later: laterData,
        weekStart: toDateKey(monday),
        laterStart: toDateKey(later),
        rows: CHECKS.map(({ key, label, hint, dated, test }) => ({
          key, label, hint, dated, ...test({ week: weekData, later: laterData })
        }))
      });
    } catch (e) {
      setCheckError(e.message || 'Could not load the timetable');
      setResults(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    getMyProfile()
      .then(result => { if (!cancelled) setProfile(result); })
      .catch(() => { if (!cancelled) setProfile(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { runChecks(); }, [runChecks]);

  // A dated check failing on its own means the fixtures have aged out, not that the page
  // broke — the seed pins them to the week it ran in.
  const staleOnly = useMemo(() => {
    if (!results) return false;
    const failed = results.rows.filter(row => !row.ok);
    return failed.length > 0 && failed.every(row => row.dated);
  }, [results]);

  const failures = results ? results.rows.filter(row => !row.ok).length : 0;

  return (
    <>
      <Page $flush={tab !== 'guide'}>
        <Head>
          <Title>Timetable — guide and demo data</Title>
          <Lede>
            What the two timetable pages do, who may do what on them, and a live check that the
            Test Demo campus still holds the awkward cases worth signing off against. The tabs
            below render the real pages, so resizing this window tests exactly what resizing
            theirs would.
          </Lede>
        </Head>

        <Tabs>
          <Tab $active={tab === 'guide'} onClick={() => setTab('guide')}>Guide &amp; checks</Tab>
          <Tab $active={tab === 'timetable'} onClick={() => setTab('timetable')}>Timetable</Tab>
          <Tab $active={tab === 'mine'} onClick={() => setTab('mine')}>My Timetable</Tab>
        </Tabs>

        {/* My Timetable answers "what am I teaching", so it is a teacher-only endpoint. An
            admin opening this tab would otherwise just see a bare 403 and wonder what broke. */}
        {tab === 'mine' && !isTeacher && (
          <Banner $tone="warn">
            My Timetable only has data for a teacher account — the API it reads is scoped to the
            signed-in teacher’s own classes. Sign in as one of the demo teachers below to see it
            with content.
          </Banner>
        )}

        {tab === 'guide' && (
        <Cards>
          <Card $wide>
            <CardTitle>Demo data check</CardTitle>
            <CardNote>
              Reads the current week and the week after next from the live API as you, then
              looks for each scenario the seed script plants. Green means the case is really
              there to click on.
            </CardNote>

            {checkError && <Banner $tone="warn">{checkError}</Banner>}

            {staleOnly && (
              <Banner $tone="warn">
                Only the dated scenarios are missing, which means the demo data has aged rather
                than broken — the one-off lessons are pinned to the week the seed last ran.
                Re-run <code>scripts/portal/seed_timetable_demo.py</code> to move them onto this week.
              </Banner>
            )}

            {results && failures === 0 && (
              <Banner>
                All {results.rows.length} scenarios present for the week of {results.weekStart}.
              </Banner>
            )}

            {results && results.rows.map(row => (
              <CheckRow key={row.key}>
                <CheckMark $state={row.ok ? 'pass' : 'fail'}>{row.ok ? '✓' : '!'}</CheckMark>
                <CheckBody>
                  <strong>{row.label}</strong>
                  <p>{row.detail}</p>
                  <p>{row.hint}</p>
                </CheckBody>
              </CheckRow>
            ))}

            {!results && !checkError && (
              <CheckRow>
                <CheckMark $state="idle">·</CheckMark>
                <CheckBody><strong>Checking…</strong></CheckBody>
              </CheckRow>
            )}

            <Muted>
              Signed in as {profile?.name || profile?.email || 'unknown'} ·
              {' '}roles {roles.join(', ') || 'none'} ·
              {' '}{canEdit ? 'may edit' : 'read only'}
              {results ? ` · weeks checked ${results.weekStart} and ${results.laterStart}` : ''}
            </Muted>

            <div style={{ marginTop: 12 }}>
              <Button type="button" onClick={runChecks} disabled={checking}>
                {checking ? 'Checking…' : 'Run again'}
              </Button>
            </div>
          </Card>

          <Card>
            <CardTitle>The five views</CardTitle>
            <CardNote>
              One fetch per week feeds all of them. Switching view re-renders and does not
              re-request, so filters and switching are instant.
            </CardNote>
            <DefList>
              {VIEWS.map(([name, what]) => (
                <React.Fragment key={name}>
                  <dt>{name}</dt>
                  <dd>{what}</dd>
                </React.Fragment>
              ))}
            </DefList>
          </Card>

          <Card>
            <CardTitle>Who may do what</CardTitle>
            <CardNote>
              The API decides this, not the page. A teacher who reaches a write another way
              still gets a 403.
            </CardNote>
            <DefList>
              <dt>Manager, admin, director</dt>
              <dd>Read and edit their own campuses. Drag, resize, add and cancel.</dd>
              <dt>Teacher</dt>
              <dd>Read only, own campus, opening on their own classes. Clearing the teacher filter shows the whole campus, which is how you find a free room.</dd>
              <dt>System admin, developer</dt>
              <dd>Every campus, and the only roles that can put several campuses in one grid.</dd>
              <dt>Parent</dt>
              <dd>No access. The page says so rather than erroring.</dd>
            </DefList>
          </Card>

          <Card>
            <CardTitle>Status</CardTitle>
            <CardNote>What is recorded against a single occurrence.</CardNote>
            <DefList>
              {STATUSES.map(([name, what]) => (
                <React.Fragment key={name}>
                  <dt>{name}</dt>
                  <dd>{what}</dd>
                </React.Fragment>
              ))}
            </DefList>
          </Card>

          <Card>
            <CardTitle>Lesson type</CardTitle>
            <CardNote>
              What one occurrence is. The class type — Group, 1:1, 1:2–3 — is a property of the
              course group and is a different thing.
            </CardNote>
            <DefList>
              {TYPES.map(([name, what]) => (
                <React.Fragment key={name}>
                  <dt>{name}</dt>
                  <dd>{what}</dd>
                </React.Fragment>
              ))}
            </DefList>
          </Card>

          {canEdit && (
            <Card $wide>
              <CardTitle>Editing, and the question it asks you</CardTitle>
              <CardNote>
                Dragging a recurring class asks whether you mean this week or the whole term.
                That question is the whole design: this week writes an override and leaves the
                course group alone, so billing, credits and materials never notice. Whole term
                rewrites the recurrence.
              </CardNote>
              <Steps>
                <li>
                  <strong>Move one week.</strong> In Room View, drag a class to another room or
                  time and choose <em>this week only</em>. The card moves; the same class next
                  week does not. <em>Check it on the Next arrow.</em>
                </li>
                <li>
                  <strong>Move the term.</strong> Drag the same class and choose <em>whole term</em>.
                  Now next week moves too, and any later override that pointed at the old slot is
                  re-aimed at the new one.
                </li>
                <li>
                  <strong>Change the day.</strong> Drag a card onto a different day tab.
                </li>
                <li>
                  <strong>Change the length.</strong> Drag the bottom edge of a card.
                </li>
                <li>
                  <strong>Cancel.</strong> Open a class and cancel it. It stays on the grid struck
                  through and its room frees up — the clash count drops if it was clashing.
                </li>
                <li>
                  <strong>Add.</strong> Click empty space in a room column, or use Add Lesson.
                  A one-off with no course group is how trials and assessments are booked.
                </li>
                <li>
                  <strong>Swap the teacher.</strong> In Teacher View, drag a class to another
                  teacher’s column. Whole term only works if that teacher is already assigned to
                  the course group — otherwise the API refuses and says so.
                </li>
              </Steps>
            </Card>
          )}

          <Card $wide>
            <CardTitle>Worth trying on this data</CardTitle>
            <CardNote>
              The Test Demo week was built so each of these has something to land on. The check
              panel above tells you which are currently present.
            </CardNote>
            <Steps>
              <li>
                <strong>Find the free room.</strong> Room View, Wednesday. Rooms are columns; the
                gaps are the answer.
              </li>
              <li>
                <strong>Read the clashes.</strong> Turn on <em>Clashes only</em>. Two should
                remain: a room double-booked on Wednesday, a teacher in two rooms on Thursday.
              </li>
              <li>
                <strong>Colour by teacher, then by class.</strong> By teacher, one person keeps
                one colour everywhere, and the colours stay put as you page between weeks.
              </li>
              <li>
                <strong>Follow one person.</strong> Per Room → <em>A teacher</em> → Amy Chen.
                Her whole week, including the Thursday overlap.
              </li>
              <li>
                <strong>Check the room names.</strong> One class is filed under
                <code> ROOM 5 </code> and the rest under <code> Room 5 </code>. They must share a
                single column, not split into two.
              </li>
              <li>
                <strong>Open a cancelled class.</strong> Monday. It is struck through, keeps its
                note, and no longer blocks its room.
              </li>
              <li>
                <strong>Export.</strong> List View → Export CSV. The file matches the filters on
                screen, not the whole week.
              </li>
              <li>
                <strong>Sign in as a teacher.</strong> Same grid, no edit affordances, opening on
                their own classes. Clear the teacher filter and the campus appears.
              </li>
            </Steps>
          </Card>

          <Card $wide>
            <CardTitle>On a phone</CardTitle>
            <CardNote>
              Below 768px the page padding tightens and the cards stack. The grids keep their
              real column widths and scroll sideways inside their own panel — the page body
              itself never scrolls horizontally, so a sideways drag on a card is the grid moving,
              not the layout breaking.
            </CardNote>
            <DefList>
              <dt>Room / Teacher</dt>
              <dd>Columns hold a 150px minimum, so five rooms scroll. Day tabs scroll too.</dd>
              <dt>Week View</dt>
              <dd>Seven columns with a 1050px floor — always a sideways scroll on a phone. It is a tablet view.</dd>
              <dt>List View</dt>
              <dd>The one that reads properly on a narrow screen. Worth being the teacher default in your head when judging the phone experience.</dd>
              <dt>My Timetable</dt>
              <dd>Its own week grid, narrowed to 48px + 96px columns below 1024px.</dd>
            </DefList>
          </Card>

          <Card $wide>
            <CardTitle>Demo accounts</CardTitle>
            <CardNote>
              All in the Test Demo campus. Passwords are in the local credentials file that is
              not in the repository — <code>.claude/CLAUDE.md</code> or <code>AGENTS.local.md</code>.
            </CardNote>
            <Scroller>
              <Table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>What it is good for</th>
                  </tr>
                </thead>
                <tbody>
                  {ACCOUNTS.map(([username, role, note]) => (
                    <tr key={username}>
                      <td><code>{username}</code></td>
                      <td><code>{role}</code></td>
                      <td>{note}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Scroller>
            <Muted>
              {isTeacher && !canEdit
                ? 'You are signed in as a teacher, so the Timetable tab above is the read-only version.'
                : 'Sign in as one of the teacher accounts to see the read-only version of the same grid.'}
            </Muted>
          </Card>
        </Cards>
        )}
      </Page>

      {tab === 'timetable' && <Timetable />}
      {tab === 'mine' && <CampusTimetableTeacher />}
    </>
  );
};

export default TimetableGuide;
